import * as Sentry from "@sentry/react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import axios from "axios";
import { RootState } from "store/rootReducer";
import { getUrlOriginAndPath } from "utils/sentryUrlHelpers";

import { ProjectModel, projectServiceEndpoint } from "api/project";

export const useProjectListQueryKeys = {
  currentProjectList: "currentProjectList"
};

export type UseProjectListQueryKeyT = keyof typeof useProjectListQueryKeys;

export interface UseProjectListQueryParamsT {
  onSuccess?: (data) => void;
  onError?: (error) => void;
  select?;
  queryKey?: UseProjectListQueryKeyT;
  isAutoRefetching?: boolean;
  staleTime?: number;
}

export interface UseProjectListQueryReturnT {
  data: ProjectModel[];
  error;
  isError: boolean;
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

export const fetchProjectList = async () => {
  let response;
  try {
    response = await axios.get(`${projectServiceEndpoint}/projects`);
  } catch (error) {
    throw new Error("Unable to fetch project list");
  }

  // Axios rejects a real 403 above. An auth redirect can instead resolve with a
  // non-array body (e.g. a sign-in page); consumers call array methods on the
  // list, so reject it as a query error.
  if (!Array.isArray(response.data)) {
    Sentry.withScope((scope) => {
      scope.setContext("fetchProjectList", {
        status: response.status,
        contentType: response.headers?.["content-type"],
        dataType: response.data === null ? "null" : typeof response.data,
        responseURL: getUrlOriginAndPath(response.request?.responseURL)
      });
      scope.setLevel("error");
      Sentry.captureMessage("Invalid project list response: expected an array");
    });
    throw new Error("Invalid project list response: expected an array");
  }

  return response;
};

export function useProjectListQuery(
  params: UseProjectListQueryParamsT = {}
): UseProjectListQueryReturnT {
  const { onError, onSuccess, isAutoRefetching = true } = params;
  const userId = useSelector((state: RootState) => state.auth?.user?.id);

  const queryInfo = useQuery({
    queryKey: [useProjectListQueryKeys.currentProjectList, userId],
    queryFn: fetchProjectList,
    onError: (error) => {
      onError && onError(error);
    },
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    enabled: isAutoRefetching,
    staleTime: params.staleTime
  });

  return {
    data: queryInfo.data?.data,
    error: queryInfo.error,
    isError: queryInfo.status === "error",
    isIdle: queryInfo.status === "idle",
    isLoading: queryInfo.status === "loading",
    isSuccess: queryInfo.status === "success",
    refetch: queryInfo.refetch
  };
}
